import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, TriangleAlertIcon } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { StateScreen } from '../../components/StateScreen';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { useLocale } from '../../contexts/LocaleContext';
import { homePath } from '../../utils/clientReview';
type Step = 'info' | 'confirm' | 'password' | 'final' | 'done';
export function DeleteAccount() {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('info');
  const [understood, setUnderstood] = useState(false);
  const [password, setPassword] = useState('');
  if (step === 'done') {
    return <StateScreen tag={t('delete.doneTag')} title={t('delete.doneTitle')} body={t('delete.doneBody')} icon={<CheckIcon size={26} data-id="element-3744" data-mp="3hlkxjklh5k55" />} tone="dark" primary={{
      label: t('delete.doneCta'),
      onClick: () => navigate(homePath())
    }} data-id="element-3743" data-mp="3mjw7rwfolwnw" />;
  }
  return <div className="px-2 pb-2" data-id="element-3745" data-mp="iloop6dk4g7h">
      <PageHeader title={t('settings.delete')} showBack data-id="element-3746" data-mp="ikvchpvtwyhy" />

      {step === 'info' && <>
          <Card className="p-2" data-id="element-3747" data-mp="mkxddwtqpnw6">
            <SectionTitle data-id="element-3748" data-mp="rcoyvsy1h3fg">{t('delete.whatHappens')}</SectionTitle>
            <ul className="space-y-1" data-id="element-3749" data-mp="rf1omsa1gi32">
              {['delete.p1', 'delete.p2', 'delete.p3', 'delete.p4'].map(key => <li key={key} className="flex items-start gap-2 text-[12.5px] leading-snug text-deep" data-id="element-3750" data-mp="11b4ofrhf44mu">
                  <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-pill bg-gold" aria-hidden="true" data-id="element-3751" data-mp="16apns30i2f03" />
                  {t(key)}
                </li>)}
            </ul>
          </Card>

          <Card className="mt-2 p-2" data-id="element-3752" data-mp="3c2ebs0f6mf5i">
            <p className="text-[12.5px] leading-relaxed text-deep" data-id="element-3753" data-mp="3c3g036l7ky56">
              {t('delete.alternative')}
            </p>
            <Button variant="primary" fullWidth className="mt-2" onClick={() => navigate('/config/dados')} data-id="element-3754" data-mp="jaiptryq0q97">
              {t('account.export')}
            </Button>
          </Card>

          <Button variant="outline" size="lg" fullWidth className="mt-3" onClick={() => setStep('confirm')} data-id="element-3755" data-mp="o6pr37g69dkt">
            {t('delete.continue')}
          </Button>
        </>}

      {step === 'confirm' && <>
          <Card className="p-2" data-id="element-3756" data-mp="1bwbopqgwbkqp">
            <SectionTitle data-id="element-3757" data-mp="1bzfwextkkym4">{t('delete.confirmTitle')}</SectionTitle>
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-3758" data-mp="16zl6awhmcd24">
              {t('delete.confirmBody')}
            </p>
            <label className="mt-2 flex items-start gap-2" data-id="element-3759" data-mp="1kyb3ec4fucqh">
              <input type="checkbox" checked={understood} onChange={event => setUnderstood(event.target.checked)} className="mt-[3px] h-5 w-5 shrink-0 accent-[color:var(--bee-gold)]" data-id="element-3760" data-mp="1fzq54i8qy34o" />
              <span className="text-[12.5px] leading-snug text-deep" data-id="element-3761" data-mp="1qk26agqkzkob">
                {t('delete.checkbox')}
              </span>
            </label>
          </Card>

          <Button variant="outline" size="lg" fullWidth className="mt-3" disabled={!understood} onClick={() => setStep('password')} data-id="element-3762" data-mp="1uju7u9hk4qk6">
            {t('delete.continue')}
          </Button>
        </>}

      {step === 'password' && <>
          <Card className="p-2" data-id="element-3763" data-mp="1bv5ss45r9fgb">
            <SectionTitle data-id="element-3764" data-mp="1bw7ed2kedkca">{t('delete.passwordTitle')}</SectionTitle>
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-3765" data-mp="xvmv65aklmi9">
              {t('delete.passwordBody')}
            </p>
            <input type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder={t('delete.passwordHint')} aria-label={t('delete.passwordTitle')} className="mt-2 h-11 w-full rounded-card border border-sand-line bg-white/80 px-2 text-[13.5px] text-deep placeholder:text-muted focus:border-deep/40" data-id="element-3766" data-mp="12ur5x0d57sap" />
          </Card>

          <Button variant="outline" size="lg" fullWidth className="mt-3" disabled={password.trim().length < 4} onClick={() => setStep('final')} data-id="element-3767" data-mp="h0kcpzxrl631">
            {t('delete.continue')}
          </Button>
        </>}

      {step === 'final' && <>
          <Card tone="dark" className="rounded-panel p-3" data-id="element-3768" data-mp="3u9os9tqo1u1g">
            <span className="grid h-12 w-12 place-items-center rounded-pill border border-gold/45 bg-deeper text-gold" data-id="element-3769" data-mp="3ub9mtjczdczb">
              <TriangleAlertIcon size={20} data-id="element-3770" data-mp="1shtlnsj9ofq3" />
            </span>
            <h2 className="mt-2 text-title text-sand" data-id="element-3771" data-mp="1sffone510tfw">{t('delete.finalTitle')}</h2>
            <p className="mt-1 text-[13px] leading-relaxed text-ondark" data-id="element-3772" data-mp="3aenelf5c7cfs">
              {t('delete.finalBody')}
            </p>
          </Card>

          <Button variant="danger" size="lg" fullWidth className="mt-3" onClick={() => setStep('done')} data-id="element-3773" data-mp="3k58c0z99yax4">
            {t('delete.finalCta')}
          </Button>
          <Button variant="outline" size="lg" fullWidth className="mt-2" onClick={() => navigate('/config')} data-id="element-3774" data-mp="2rhcj85mtt35m">
            {t('delete.keepAccount')}
          </Button>
        </>}
    </div>;
}